import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import {
  getTrainingPlanDetailById,
  updateTrainingPlanById,
  createTrainingWeek,
  updateTrainingWeek,
  deleteTrainingWeek,
  createTrainingDay,
  updateTrainingDay,
  deleteTrainingDay,
  createTrainingBlock,
  updateTrainingBlock,
  updateTrainingBlockPositions,
  deleteTrainingBlock,
  createTrainingStation,
  updateTrainingStation,
  deleteTrainingStation,
  addTrainingStationDrillsWithPositions,
  updateTrainingStationDrillPositions,
  moveTrainingStationDrills,
  deleteTrainingStationDrill,
  deleteTrainingStationDrills,
  duplicateTrainingWeek,
  duplicateTrainingDay,
  duplicateTrainingBlock,
  duplicateTrainingStation,
  syncDrillMetrics as syncDrillMetricsApi
} from 'API/training-plan'
import {
  ITrainingBlock,
  ITrainingDay,
  ITrainingDrill,
  ITrainingDrillMetric,
  ITrainingPlanDetail,
  ITrainingStation,
  ITrainingWeek
} from 'interfaces/training-plan'
import { TrainingComponentType } from 'interfaces/training-component'

class TrainingPlanDetailStore {
  rootStore: RootStore
  trainingPlanDetail: ITrainingPlanDetail | null = null
  isFetchingDetail = false
  isSilentOperation = false
  isManageMode = false
  isEditMode = false
  isSaving = false
  isDraggingDrill = false
  isMovingDrills = false

  get isBlockingInteractions(): boolean {
    return this.isDraggingDrill || this.isMovingDrills
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  setManageMode(value: boolean): void {
    this.isManageMode = value
    if (!value) this.isEditMode = false
  }

  setEditMode(value: boolean): void {
    this.isEditMode = value
  }

  setIsDraggingDrill(value: boolean): void {
    this.isDraggingDrill = value
  }

  setIsMovingDrills(value: boolean): void {
    this.isMovingDrills = value
  }

  private async withSilentOperation<T>(operation: () => Promise<T>): Promise<T> {
    this.isSilentOperation = true
    try {
      return await operation()
    } finally {
      this.isSilentOperation = false
    }
  }

  // Refetch the plan detail without flashing the full-page spinner. Public so
  // callers (e.g. drag-drop handlers) reconcile through the store's action
  // instead of poking `isSilentOperation` directly.
  async reloadDetailSilently(id: number): Promise<void> {
    await this.withSilentOperation(() => this.getTrainingPlanDetail(id))
  }

  // Decreasing counter for optimistic node ids — negative so they never collide
  // with real (positive) ids and are trivial to spot / replace on reload.
  private optimisticIdSeq = -1

  nextOptimisticId(): number {
    return this.optimisticIdSeq--
  }

  // Optimistically insert a cloned-component node into the training tree so a
  // drag-drop from the builder library shows INSTANTLY, before the backend clone
  // finishes; reloadDetailSilently() then swaps the tree for authoritative data
  // (dropping the temp negative-id node) on success, or reverts it on failure.
  //
  // Rebuilds `weeks` immutably (new references along the insertion path) so the
  // builder's `periods` useMemo — keyed on the `weeks` reference — recomputes and
  // the optimistic node actually renders (an in-place push would not trigger it).
  insertOptimisticNode(
    parentType: 'trainingplan' | TrainingComponentType,
    parentId: number,
    _position: number,
    node: ITrainingWeek | ITrainingDay | ITrainingBlock | ITrainingStation
  ): void {
    const detail = this.trainingPlanDetail
    if (!detail?.weeks) return

    const insertSorted = <T extends { position?: number }>(arr: T[], item: T): T[] =>
      [...arr, item].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

    if (parentType === 'trainingplan') {
      this.trainingPlanDetail = { ...detail, weeks: insertSorted(detail.weeks, node as ITrainingWeek) }
      return
    }
    if (parentType === 'week') {
      this.trainingPlanDetail = {
        ...detail,
        weeks: detail.weeks.map(week =>
          week._id === parentId ? { ...week, days: insertSorted(week.days ?? [], node as ITrainingDay) } : week
        )
      }
      return
    }
    if (parentType === 'day') {
      this.trainingPlanDetail = {
        ...detail,
        weeks: detail.weeks.map(week => ({
          ...week,
          days: (week.days ?? []).map(day =>
            day._id === parentId ? { ...day, blocks: insertSorted(day.blocks ?? [], node as ITrainingBlock) } : day
          )
        }))
      }
      return
    }
    // parentType === 'block' (a block holds stations). 'station' never appears as
    // a parent (nothing nests under a station), so any other value is a no-op.
    if (parentType === 'block') {
      this.trainingPlanDetail = {
        ...detail,
        weeks: detail.weeks.map(week => ({
          ...week,
          days: (week.days ?? []).map(day => ({
            ...day,
            blocks: (day.blocks ?? []).map(block =>
              block._id === parentId
                ? { ...block, stations: insertSorted(block.stations ?? [], node as ITrainingStation) }
                : block
            )
          }))
        }))
      }
    }
  }

  async getTrainingPlanDetail(id: number): Promise<void> {
    if (!this.isSilentOperation) {
      this.isFetchingDetail = true
    }
    try {
      this.trainingPlanDetail = await getTrainingPlanDetailById(id)
    } finally {
      if (!this.isSilentOperation) this.isFetchingDetail = false
    }
  }

  // Immutable in-place update for optimistic DnD changes
  updateStationDrills(stationId: number, newDrills: ITrainingDrill[]): void {
    if (!this.trainingPlanDetail?.weeks) return
    this.trainingPlanDetail = {
      ...this.trainingPlanDetail,
      weeks: this.trainingPlanDetail.weeks.map(week => ({
        ...week,
        days: (week.days || []).map(day => ({
          ...day,
          blocks: (day.blocks || []).map(block => ({
            ...block,
            stations: (block.stations || []).map(station => {
              if (Number(station._id) !== Number(stationId)) return station
              return { ...station, drills: newDrills }
            })
          }))
        }))
      }))
    }
  }

  async publish(planId: number): Promise<void> {
    this.isSaving = true
    try {
      await updateTrainingPlanById(planId, { status: 1 })
      await this.getTrainingPlanDetail(planId)
      this.isEditMode = false
      this.isManageMode = false
    } finally {
      this.isSaving = false
    }
  }

  async createWeek(trainingPlanId: number, name: string): Promise<void> {
    await createTrainingWeek({ trainingPlanId, name })
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async updateWeek(
    id: number,
    trainingPlanId: number,
    data: { name?: string; objective?: string; note?: string }
  ): Promise<void> {
    await updateTrainingWeek(id, data)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  async deleteWeek(id: number, trainingPlanId: number): Promise<void> {
    await deleteTrainingWeek(id)
    // Use silent refresh so TrainingPlanDetailPage doesn't show global spinner
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async createDay(trainingWeekId: number, trainingPlanId: number, name: string): Promise<void> {
    await createTrainingDay({ trainingWeekId, name })
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async updateDay(
    id: number,
    trainingPlanId: number,
    data: { name?: string; note?: string }
  ): Promise<void> {
    await updateTrainingDay(id, data)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  async deleteDay(id: number, trainingPlanId: number): Promise<void> {
    await deleteTrainingDay(id)
    // Use silent refresh so TrainingPlanDetailPage doesn't show global spinner
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async createBlock(trainingDayId: number, trainingPlanId: number, name: string): Promise<void> {
    await createTrainingBlock({ trainingDayId, name })
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async updateBlock(
    id: number,
    trainingPlanId: number,
    data: { name?: string; note?: string; estimatedDuration?: number }
  ): Promise<void> {
    await updateTrainingBlock(id, data)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  async deleteBlock(id: number, trainingPlanId: number): Promise<void> {
    await deleteTrainingBlock(id)
    // Use silent refresh so TrainingPlanDetailPage doesn't show global spinner
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async createStation(trainingBlockId: number, trainingPlanId: number, name: string): Promise<void> {
    await createTrainingStation({ trainingBlockId, name })
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  async updateStation(
    id: number,
    trainingPlanId: number,
    data: { name?: string; objective?: string; note?: string }
  ): Promise<void> {
    await updateTrainingStation(id, data)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  async deleteStation(id: number, trainingPlanId: number): Promise<void> {
    await deleteTrainingStation(id)
    // Use silent refresh so TrainingPlanDetailPage doesn't show global spinner
    await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
  }

  // Optimistic update → API → silent refresh (mirrors Practice Plan addStepsToEndOfStation)
  // insertIndex: when provided (drag-drop), inserts at exact position and re-normalizes all positions
  async addDrills(
    trainingStationId: number,
    trainingPlanId: number,
    drillIds: number[],
    insertIndex?: number,
    drillNamesById?: Record<number, string>,
    onOptimisticApplied?: (optimisticDrillIds: number[]) => void
  ): Promise<void> {
    if (!drillIds.length) return
    const station = this.findStation(trainingStationId)
    const existingDrills = station?.drills || []

    const timestamp = Date.now()
    const tempDrills: ITrainingDrill[] = drillIds.map((drillId, index) => {
      const stepInfo = this.rootStore.planStore.selectedProcessSteps.get(drillId)
      const providedName = drillNamesById?.[drillId]
      return {
        _id: -1 * (timestamp + index),
        drillId,
        name: providedName || stepInfo?.name || `Drill ${drillId}`,
        position: 0 // will be normalized below
      }
    })

    // Determine insert position: drag path uses insertIndex, button path appends to end
    const safeIndex = insertIndex !== undefined
      ? Math.max(0, Math.min(insertIndex, existingDrills.length))
      : existingDrills.length

    const spliced = [
      ...existingDrills.slice(0, safeIndex),
      ...tempDrills,
      ...existingDrills.slice(safeIndex)
    ]
    // Normalize 1-based positions
    const normalized = spliced.map((d, idx) => ({ ...d, position: idx + 1 }))
    this.updateStationDrills(trainingStationId, normalized)
    onOptimisticApplied?.(tempDrills.map(drill => drill._id))

    // New drills get positions safeIndex+1 … safeIndex+N
    const newDrillPositions = drillIds.map((drillId, index) => ({
      drillId,
      position: safeIndex + index + 1,
      isNew: true
    }))

    // Existing drills that shifted right need their positions updated
    const shiftedDrills = existingDrills.slice(safeIndex).map((d, index) => ({
      id: d._id,
      position: safeIndex + drillIds.length + index + 1
    }))

    try {
      const response = await addTrainingStationDrillsWithPositions({
        trainingStationId,
        drillPositions: newDrillPositions,
        existingDrillPositions: shiftedDrills
      })

      if (response?.stationId === trainingStationId && Array.isArray(response.drills)) {
        this.updateStationDrills(trainingStationId, response.drills as ITrainingDrill[])
      }
    } catch (error) {
      // Full (non-silent) refresh on error
      await this.getTrainingPlanDetail(trainingPlanId)
      throw error
    }
  }

  // Optimistic update → API → no refresh on success (mirrors Practice Plan updateStepPositions)
  async reorderDrills(
    trainingPlanId: number,
    stationId: number,
    drillPositions: Array<{ id: number; position: number }>
  ): Promise<void> {
    const station = this.findStation(stationId)
    if (station) {
      const reordered = [...station.drills]
        .map(d => {
          const updated = drillPositions.find(dp => Number(dp.id) === Number(d._id))
          return updated ? { ...d, position: updated.position } : d
        })
        .sort((a, b) => (a.position || 0) - (b.position || 0))
      this.updateStationDrills(stationId, reordered)
    }

    try {
      await updateTrainingStationDrillPositions({ trainingStationId: stationId, drillPositions })
    } catch (error) {
      await this.getTrainingPlanDetail(trainingPlanId)
      throw error
    }
    // No refresh on success — optimistic state is correct
  }

  // Optimistic update → API → rollback on failure (mirrors reorderDrills pattern)
  async reorderBlocks(
    trainingPlanId: number,
    dayId: number,
    blockPositions: Array<{ id: number; position: number }>
  ): Promise<void> {
    if (!this.trainingPlanDetail?.weeks) return

    const snapshot = this.trainingPlanDetail

    this.trainingPlanDetail = {
      ...this.trainingPlanDetail,
      weeks: this.trainingPlanDetail.weeks.map(week => ({
        ...week,
        days: (week.days || []).map(day => {
          if (Number(day._id) !== Number(dayId)) return day
          const reorderedBlocks = [...(day.blocks || [])]
            .map(block => {
              const updated = blockPositions.find(bp => Number(bp.id) === Number(block._id))
              return updated ? { ...block, position: updated.position } : block
            })
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          return { ...day, blocks: reorderedBlocks }
        })
      }))
    }

    try {
      await updateTrainingBlockPositions(blockPositions)
    } catch (error) {
      this.trainingPlanDetail = snapshot
      throw error
    }
    // No refresh on success — optimistic state is correct
  }

  // API only — builder does optimistic update before calling this (mirrors Practice Plan moveStepBetweenStations)
  async moveDrills(
    trainingPlanId: number,
    drillIds: number[],
    fromStationId: number,
    toStationId: number,
    newStationDrillPositions: Array<{ id: number; position: number }>
  ): Promise<void> {
    try {
      await moveTrainingStationDrills({
        drillIds,
        fromStationId,
        toStationId,
        newStationDrillPositions
      })
    } catch (error) {
      await this.getTrainingPlanDetail(trainingPlanId)
      throw error
    }
    // No refresh on success — builder's optimistic update is already correct
  }

  async deleteDrill(id: number, trainingPlanId: number): Promise<void> {
    await deleteTrainingStationDrill(id)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  async deleteDrills(ids: number[], trainingPlanId: number): Promise<void> {
    if (!ids.length) return
    const firstDrillId = Number(ids[0])
    let stationId: number | null = null
    if (this.trainingPlanDetail?.weeks) {
      for (const week of this.trainingPlanDetail.weeks) {
        for (const day of week.days || []) {
          for (const block of day.blocks || []) {
            for (const station of block.stations || []) {
              if ((station.drills || []).some(drill => Number(drill._id) === firstDrillId)) {
                stationId = Number(station._id)
                break
              }
            }
            if (stationId !== null) break
          }
          if (stationId !== null) break
        }
        if (stationId !== null) break
      }
    }
    if (stationId === null) {
      throw new Error('Station not found for selected drills')
    }
    await deleteTrainingStationDrills(stationId, ids)
    this.isSilentOperation = true
    try {
      await this.getTrainingPlanDetail(trainingPlanId)
    } finally {
      this.isSilentOperation = false
    }
  }

  // Move operations - update parent ID + position via existing PATCH endpoints
  async moveStation(
    stationId: number,
    targetBlockId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      const sourceBlock = this.trainingPlanDetail?.weeks
        ?.flatMap(week => week.days || [])
        ?.flatMap(day => day.blocks || [])
        ?.find(block => (block.stations || []).some(station => station._id === stationId))
      const sourceStation = sourceBlock?.stations?.find(station => station._id === stationId)
      const sourceBlockId = sourceBlock?._id

      const targetBlock = this.trainingPlanDetail?.weeks
        ?.flatMap(week => week.days || [])
        ?.flatMap(day => day.blocks || [])
        ?.find(block => block._id === targetBlockId)

      if (!targetBlock || !sourceStation) return false

      const isSameBlock = sourceBlockId === targetBlockId
      const targetStations = (targetBlock.stations || []).filter(station => station._id !== stationId)
      const sourceStationPosition = sourceStation.position || 0
      const newPosition = position === 'end'
        ? (targetStations.length > 0 ? Math.max(...targetStations.map(station => station.position || 0)) + 1 : 1)
        : 1

      if (isSameBlock && position === 'beginning') {
        const beforeStations = targetStations.filter(station => (station.position || 0) < sourceStationPosition)
        const afterStations = targetStations.filter(station => (station.position || 0) > sourceStationPosition)

        await updateTrainingStation(stationId, { trainingBlockId: targetBlockId, position: 1 })
        await Promise.all([
          ...beforeStations.map(station => updateTrainingStation(station._id, { position: (station.position || 0) + 1 })),
          ...afterStations.map(station => updateTrainingStation(station._id, { position: (station.position || 0) - 1 }))
        ])
      } else {
        if (sourceBlockId && !isSameBlock) {
          const sourceStations = (sourceBlock?.stations || []).filter(station => station._id !== stationId)
          await Promise.all(
            sourceStations
              .filter(station => (station.position || 0) > sourceStationPosition)
              .map(station => updateTrainingStation(station._id, { position: (station.position || 0) - 1 }))
          )
        }

        if (position === 'beginning' && targetStations.length > 0) {
          await updateTrainingStation(stationId, { trainingBlockId: targetBlockId, position: 1 })
          await Promise.all(
            targetStations.map(station => updateTrainingStation(station._id, { position: (station.position || 0) + 1 }))
          )
        } else {
          await updateTrainingStation(stationId, { trainingBlockId: targetBlockId, position: newPosition })
        }
      }

      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async moveBlock(
    blockId: number,
    targetDayId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      const sourceDay = this.trainingPlanDetail?.weeks
        ?.flatMap(week => week.days || [])
        ?.find(day => (day.blocks || []).some(block => block._id === blockId))
      const sourceBlock = sourceDay?.blocks?.find(block => block._id === blockId)
      const sourceDayId = sourceDay?._id

      const targetDay = this.trainingPlanDetail?.weeks
        ?.flatMap(week => week.days || [])
        ?.find(day => day._id === targetDayId)

      if (!targetDay || !sourceBlock) return false

      const isSameDay = sourceDayId === targetDayId
      const targetBlocks = (targetDay.blocks || []).filter(block => block._id !== blockId)
      const sourceBlockPosition = sourceBlock.position || 0
      const newPosition = position === 'end'
        ? (targetBlocks.length > 0 ? Math.max(...targetBlocks.map(block => block.position || 0)) + 1 : 1)
        : 1

      if (isSameDay && position === 'beginning') {
        const beforeBlocks = targetBlocks.filter(block => (block.position || 0) < sourceBlockPosition)
        const afterBlocks = targetBlocks.filter(block => (block.position || 0) > sourceBlockPosition)

        await updateTrainingBlock(blockId, { trainingDayId: targetDayId, position: 1 })
        await Promise.all([
          ...beforeBlocks.map(block => updateTrainingBlock(block._id, { position: (block.position || 0) + 1 })),
          ...afterBlocks.map(block => updateTrainingBlock(block._id, { position: (block.position || 0) - 1 }))
        ])
      } else {
        if (sourceDayId && !isSameDay) {
          const sourceBlocks = (sourceDay?.blocks || []).filter(block => block._id !== blockId)
          await Promise.all(
            sourceBlocks
              .filter(block => (block.position || 0) > sourceBlockPosition)
              .map(block => updateTrainingBlock(block._id, { position: (block.position || 0) - 1 }))
          )
        }

        if (position === 'beginning' && targetBlocks.length > 0) {
          await updateTrainingBlock(blockId, { trainingDayId: targetDayId, position: 1 })
          await Promise.all(
            targetBlocks.map(block => updateTrainingBlock(block._id, { position: (block.position || 0) + 1 }))
          )
        } else {
          await updateTrainingBlock(blockId, { trainingDayId: targetDayId, position: newPosition })
        }
      }

      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async moveDay(
    dayId: number,
    targetWeekId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      const sourceWeek = this.trainingPlanDetail?.weeks?.find(week => (week.days || []).some(day => day._id === dayId))
      const sourceDay = sourceWeek?.days?.find(day => day._id === dayId)
      const sourceWeekId = sourceWeek?._id

      const targetWeek = this.trainingPlanDetail?.weeks?.find(week => week._id === targetWeekId)
      if (!targetWeek || !sourceDay) return false

      const isSameWeek = sourceWeekId === targetWeekId
      const targetDays = (targetWeek.days || []).filter(day => day._id !== dayId)
      const sourceDayPosition = sourceDay.position || 0
      const newPosition = position === 'end'
        ? (targetDays.length > 0 ? Math.max(...targetDays.map(day => day.position || 0)) + 1 : 1)
        : 1

      if (isSameWeek && position === 'beginning') {
        const beforeDays = targetDays.filter(day => (day.position || 0) < sourceDayPosition)
        const afterDays = targetDays.filter(day => (day.position || 0) > sourceDayPosition)

        await updateTrainingDay(dayId, { trainingWeekId: targetWeekId, position: 1 })
        await Promise.all([
          ...beforeDays.map(day => updateTrainingDay(day._id, { position: (day.position || 0) + 1 })),
          ...afterDays.map(day => updateTrainingDay(day._id, { position: (day.position || 0) - 1 }))
        ])
      } else {
        if (sourceWeekId && !isSameWeek) {
          const sourceDays = (sourceWeek?.days || []).filter(day => day._id !== dayId)
          await Promise.all(
            sourceDays
              .filter(day => (day.position || 0) > sourceDayPosition)
              .map(day => updateTrainingDay(day._id, { position: (day.position || 0) - 1 }))
          )
        }

        if (position === 'beginning' && targetDays.length > 0) {
          await updateTrainingDay(dayId, { trainingWeekId: targetWeekId, position: 1 })
          await Promise.all(
            targetDays.map(day => updateTrainingDay(day._id, { position: (day.position || 0) + 1 }))
          )
        } else {
          await updateTrainingDay(dayId, { trainingWeekId: targetWeekId, position: newPosition })
        }
      }

      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  // Duplicate operations - use dedicated BE endpoints
  async duplicateWeekAction(
    weekId: number,
    targetPlanId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      await duplicateTrainingWeek(weekId, targetPlanId, position)
      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async duplicateDayAction(
    dayId: number,
    targetWeekId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      await duplicateTrainingDay(dayId, targetWeekId, position)
      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async duplicateBlockAction(
    blockId: number,
    targetDayId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      await duplicateTrainingBlock(blockId, targetDayId, position)
      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async duplicateStationAction(
    stationId: number,
    targetBlockId: number,
    position: 'beginning' | 'end',
    trainingPlanId: number
  ): Promise<boolean> {
    try {
      await duplicateTrainingStation(stationId, targetBlockId, position)
      await this.withSilentOperation(() => this.getTrainingPlanDetail(trainingPlanId))
      return true
    } catch {
      await this.getTrainingPlanDetail(trainingPlanId)
      return false
    }
  }

  async syncDrillMetrics(
    drillId: number,
    metrics: Array<{ metricId: number; required?: boolean }>
  ): Promise<ITrainingDrillMetric[]> {
    const result = await syncDrillMetricsApi(drillId, metrics)
    // Update local state with new metrics
    if (this.trainingPlanDetail?.weeks) {
      this.trainingPlanDetail = {
        ...this.trainingPlanDetail,
        weeks: this.trainingPlanDetail.weeks.map(week => ({
          ...week,
          days: (week.days || []).map(day => ({
            ...day,
            blocks: (day.blocks || []).map(block => ({
              ...block,
              stations: (block.stations || []).map(station => ({
                ...station,
                drills: (station.drills || []).map(drill => {
                  if (Number(drill._id) !== Number(drillId)) return drill
                  return { ...drill, metrics: result }
                })
              }))
            }))
          }))
        }))
      }
    }
    const planId = Number(this.trainingPlanDetail?._id ?? this.trainingPlanDetail?.id)
    if (Number.isFinite(planId) && planId > 0) {
      await this.withSilentOperation(() => this.getTrainingPlanDetail(planId))
    }
    return result
  }

  findStation(stationId: number): { drills: ITrainingDrill[] } | null {
    if (!this.trainingPlanDetail?.weeks) return null
    for (const week of this.trainingPlanDetail.weeks) {
      for (const day of week.days || []) {
        for (const block of day.blocks || []) {
          for (const station of block.stations || []) {
            if (Number(station._id) === Number(stationId)) {
              return { drills: station.drills || [] }
            }
          }
        }
      }
    }
    return null
  }

  findWeek(weekId: number): { days: Array<{ _id: number; position: number }> } | null {
    if (!this.trainingPlanDetail?.weeks) return null
    const week = this.trainingPlanDetail.weeks.find(w => Number(w._id) === Number(weekId))
    if (!week) return null
    return { days: (week.days || []).map(d => ({ _id: d._id, position: d.position })) }
  }

  findDay(dayId: number): { blocks: Array<{ _id: number; position: number }> } | null {
    if (!this.trainingPlanDetail?.weeks) return null
    for (const week of this.trainingPlanDetail.weeks) {
      const day = (week.days || []).find(d => Number(d._id) === Number(dayId))
      if (day) return { blocks: (day.blocks || []).map(b => ({ _id: b._id, position: b.position })) }
    }
    return null
  }

  findBlock(blockId: number): { stations: Array<{ _id: number; position: number }> } | null {
    if (!this.trainingPlanDetail?.weeks) return null
    for (const week of this.trainingPlanDetail.weeks) {
      for (const day of week.days || []) {
        const block = (day.blocks || []).find(b => Number(b._id) === Number(blockId))
        if (block) return { stations: (block.stations || []).map(s => ({ _id: s._id, position: s.position })) }
      }
    }
    return null
  }
}

export default TrainingPlanDetailStore
